/**
 * How long a write waits for the re-read that follows it before handing the screen back.
 *
 * Long enough for the re-read of an ordinary list or detail to land, so the overlay and the held-open
 * dialog lift together with the new state. Short enough that a stalled re-read cannot freeze the
 * console: the admin client allows 90 seconds per request (`API_TIMEOUT`) and queries retry once, so
 * an uncapped wait could keep a page-blocking overlay up for about three minutes over a change that
 * had already been saved.
 */
export const REFRESH_WAIT_CAP_MS = 8000;

/**
 * Waits for a post-write refresh — usually `Promise.all` of `invalidateQueries` — but never longer
 * than `capMs`, and never rejects.
 *
 * Return it from a mutation's `onSettled`. React Query awaits that callback before `isPending`
 * clears, which is what keeps the spinner, the dialog and the overlay up until fresh data is on
 * screen. Past the cap the UI is released anyway; the refetch keeps running and the screen updates
 * when it lands. A refresh failure is not the write's failure — the change is already saved — so it
 * resolves rather than turning a success into an error.
 */
export function awaitRefresh(
  work: Promise<unknown>,
  capMs: number = REFRESH_WAIT_CAP_MS,
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cap = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, capMs);
  });
  const settled = work.then(
    () => undefined,
    () => undefined,
  );

  return Promise.race([settled, cap]).finally(() => clearTimeout(timer));
}
